import { PAGE_INFO_FIELDS } from "@/graphql/pagination";
import gql from "graphql-tag";

export const ORGANISATION_ORDER_SUMMARY_SOURCE_BASKET_FIELDS = gql`
  fragment OrganisationOrderSummarySourceBasketFields on OrganisationBasketType {
    id
    payee
    paymentStatus
    orderStatus
    basketTotalPrice
    currencyName
    currencySymbol
    paymentInvoice {
      id
      stripeId
      paymentStatus
      url
      invoiceUrl
      dateDue
    }
    paymentCheckoutSession {
      id
      stripeId
      paymentStatus
      url
      invoiceUrl
    }
  }
`;

export const ORGANISATION_ORDER_BALANCE_QUERY = gql`
  query OrganisationOrderBalanceQuery($organisation: Int!) {
    orderBalance(organisation: $organisation) {
      currencySymbol
      value
    }
  }
`;

export const ORGANISATION_ORDERS_LIST_QUERY = gql`
  query OrganisationOrdersListQuery(
    $organisation: Int!
    $page: Int
    $pageSize: Int
    $orderBy: String
    $search: String
    $filter: String
  ) {
    organisationOrdersPagination(
      organisation: $organisation
      page: $page
      pageSize: $pageSize
      orderBy: $orderBy
      search: $search
      filter: $filter
    ) {
      items {
        id
        checkoutDate
        estimatedResultsAvailableDate
        estimatedResultsAvailableDateHelpText
        userFullName
        userId
        derivedPaymentStatus
        derivedProgressStatus
        sourceBasket {
          ...OrganisationOrderSummarySourceBasketFields
        }
        status
        discountedTotal
        suppliedFromPracticeStock
        isStockOrder
        testSummary
      }
      pageInfo {
        ...PageInfoFields
      }
    }
  }
  ${ORGANISATION_ORDER_SUMMARY_SOURCE_BASKET_FIELDS}
  ${PAGE_INFO_FIELDS}
`;

/** Basket fields for order detail only (list view uses OrganisationOrderSummarySourceBasketFields). */
export const ORGANISATION_ORDER_DETAIL_SOURCE_BASKET_FIELDS = gql`
  fragment OrganisationOrderDetailSourceBasketFields on OrganisationBasketType {
    id
    payee
    basketTotalPrice
    currencySymbol
    paymentInvoice {
      id
      stripeId
      paymentStatus
      url
      invoiceUrl
      dateDue
    }
    paymentCheckoutSession {
      id
      stripeId
      paymentStatus
      url
      invoiceUrl
    }
    refunded
    basketTotalDiscount
    basketTotalCommission
    basketShippingCost
  }
`;

/** Test line items for CMP order detail only (narrower than OrderTestItemFields). */
export const ORGANISATION_ORDER_DETAIL_TEST_ITEM_FIELDS = gql`
  fragment OrganisationOrderDetailTestItemFields on OrderTestItemType {
    id
    price
    nameInBasket
    providerTestId
    derivedProgressStatus
    registered
    hasUserReport
    patientUserTestId
    patientUserId
    parent {
      id
    }
    estimatedResultsAvailableDate
    estimatedResultsAvailableDateHelpText
    product {
      id
      paymentIsoCode
      organisation
      sampleTypes
      isCyrex
      requiredAddons {
        id
        paymentIsoCode
        organisation
      }
    }
    clinicLocation {
      id
      paymentIsoCode
      organisation
      fee
      address
      postCode
      bookingLink
      clinic {
        id
        name
        bookingLink
      }
    }
    clinicBooking {
      id
      url
      status
      details
    }
    registeredUserFullName
  }
`;

export const ORGANISATION_ORDER_DETAIL_FIELDS = gql`
  fragment OrganisationOrderDetailFields on OrderType {
    id
    practitionerWhoPlacedOrderFullName
    orderCopiedFrom {
      id
    }
    checkoutDate
    estimatedResultsAvailableDate
    estimatedResultsAvailableDateHelpText
    testSummary
    shippingFirstName
    shippingAddress1
    shippingAddress2
    shippingTownCity
    shippingPostalCode
    shippingCountry
    shippingPhone
    userFullName
    userId
    status
    suppliedFromPracticeStock
    isStockOrder
    derivedProgressStatus
    derivedPaymentStatus
    patientPayCheckoutRedirectUrl
    sourceBasket {
      ...OrganisationOrderDetailSourceBasketFields
    }
    testItems {
      ...OrganisationOrderDetailTestItemFields
    }
    supplementItems {
      id
      nameInBasket
      price
      product {
        id
      }
    }
  }
  ${ORGANISATION_ORDER_DETAIL_SOURCE_BASKET_FIELDS}
  ${ORGANISATION_ORDER_DETAIL_TEST_ITEM_FIELDS}
`;

export const ORGANISATION_ORDER_DETAIL_QUERY = gql`
  query OrganisationOrderDetailQuery($organisation: Int!, $id: String!) {
    organisationOrder(organisation: $organisation, id: $id) {
      ...OrganisationOrderDetailFields
    }
  }
  ${ORGANISATION_ORDER_DETAIL_FIELDS}
`;
